import { escapeHtml } from '~/utils/string'

// Single source of truth for the class CmsContent puts on the element that
// wraps a rendered CMS payload. It is a contract, not an implementation
// detail: the SparkIt anchor (useAnchorToContent, AB#62164) looks the element
// up by it, and the `.content--cms` rules in the stylesheets listed below style
// by it. CmsContent itself no longer does: it finds its own content through
// template refs, so putting the class on another element gets that element
// styled, not processed. Route-path constants live in
// utils/categoryRoutes.ts and utils/portalRoutes.ts; this is neither, so it
// gets its own module rather than being smuggled into one of those.
//
// A rename is caught on the script side (sparkit.spec.ts asserts the selector
// the anchor is given) but not in the stylesheets, which cannot import this and
// spell the class out. Nothing fails if one is left behind, so rename by hand
// in each: assets/scss/global.scss, assets/scss/globals-v3.scss,
// assets/scss/lib/global.scss and the style blocks of
// components/portal/PortalBase.vue.
export const CMS_CONTENT_CLASS = 'content--cms' as const
export const CMS_CONTENT_SELECTOR = `.${CMS_CONTENT_CLASS}` as const

// Video widget Sitefinity renders (checkbox, cover label, YouTube iframe).
// useCmsVideoReveal adds the playing class. The stylesheets cannot import
// these and spell them out, so rename by hand in each: the style blocks of
// components/content/CmsContent.vue (container, cover and playing class),
// components/products/ShopProductsPage.vue and components/portal/PortalBase.vue
// (container only).
export const CMS_VIDEO_CONTAINER_CLASS = 'video-container' as const
export const CMS_VIDEO_CONTAINER_SELECTOR = `.${CMS_VIDEO_CONTAINER_CLASS}` as const
export const CMS_VIDEO_COVER_CLASS = 'cover' as const
export const CMS_VIDEO_PLAYING_CLASS = 'is-playing' as const

// The placeholder useCmsCarousel replaces with a product carousel, handing that
// component the placeholder's class as written: <div data-carousel="query"></div>,
// or self-closed. withVuetify4Classes leaves exactly these elements' classes alone,
// so both read placeholders through here. Each match: [1] the attributes before
// data-carousel, [2] the query.
const CAROUSEL_PLACEHOLDER = /<div\s+([^>]*?)data-carousel="([^"]+)"[^>]*(?:\/>|><\/div>)/g

export const cmsCarouselPlaceholders = (html: string) => html.matchAll(CAROUSEL_PLACEHOLDER)

// The wrapper the CMS puts around the homepage's below-carousel markup
// (otherContent). pages/index.vue scrolls to it and keeps the images of its
// first block out of the deferral. The CMS authors it, so a rename there
// silently turns both off.
export const CMS_OTHER_CONTENT_CLASS = 'other-content' as const
export const CMS_OTHER_CONTENT_SELECTOR = `.${CMS_OTHER_CONTENT_CLASS}` as const

// A CMS image downloads eagerly unless the CMS authored a loading attribute on
// it - including those below the fold and the copies a block hides at one
// breakpoint (display:none does not stop an eager image). Native lazy loading
// skips both until layout puts them near the viewport. An attribute the CMS did
// author is kept. Only for content that is never the LCP image: a lazy image is
// requested after layout, later than the preload scanner would find it.
const QUOTED = `"[^"]*"|'[^']*'`
const TAG_BODY = `(?:${QUOTED}|[^>"'])*`
const IMG_TAG = new RegExp(`<img\\b${TAG_BODY}>`, 'gi')

// One attribute of a tag per match: the space before it, its name, and its
// value with the `=`, quoted or not. A value is matched whole, so text inside one
// (alt="5 > 3", alt=" loading=eager", alt="src=x") is never taken for an attribute.
const ATTRIBUTE = new RegExp(`(\\s)([^\\s"'>/=]+)(\\s*=\\s*(?:${QUOTED}|[^\\s"'>]+))?`, 'g')

// The value of the tag's `name` attribute, unquoted; undefined when it has none.
const attributeValue = (tag: string, name: string): string | undefined => {
  for (const [, , attribute = '', assignment = ''] of tag.matchAll(ATTRIBUTE)) {
    if (attribute.toLowerCase() === name) {
      return assignment.replace(/^\s*=\s*/, '').replace(/^(["'])([\s\S]*)\1$/, '$2')
    }
  }
}

export const withLazyImages = (html: string): string =>
  html.replace(IMG_TAG, tag => attributeValue(tag, 'loading') === undefined
    ? tag.replace(/^<img\b/i, '<img loading="lazy"')
    : tag)

// The CMS authors loading="lazy" on every image, the first hero slide's too.
// That one is the LCP image: lazy, it is not requested until the first layout
// (after the render-blocking CSS) and then shares the link with every lazy image
// below it. Drops an authored loading="lazy"; any other value stays. A quoted
// value is matched first and kept, so text inside it is never taken for the
// attribute.
const QUOTED_OR_LAZY_LOADING = new RegExp(`(${QUOTED})|\\sloading\\s*=\\s*(?:"lazy"|'lazy'|lazy(?=[\\s/>]))`, 'gi')

export const withoutLazyLoading = (html: string): string =>
  html.replace(IMG_TAG, tag => tag.replace(QUOTED_OR_LAZY_LOADING, (_match, quoted?: string) => quoted ?? ''))

// Lazy loading alone does not keep below-the-fold images out of the first
// seconds: on a slow connection Chrome's lazy-load distance covers the whole
// homepage, so they all start at first layout and share the link with the
// scripts hydration waits for. That holds for an authored loading="lazy" too,
// so those images are deferred as well; only an authored loading="eager" (load
// it now) is left alone. Deferred images carry their src and srcset under
// data-cms-* until restoreDeferredImages puts them back after hydration. The
// sources of a <picture> are deferred with its image: an undeferred
// <source srcset> still starts the download of the candidate it selects, even
// with no src on the image. The alt stays, so an image that is all of a link's
// content still names it before hydration and without JavaScript; until the
// restore the image shows its alt text, which moves nothing in view as deferred
// images are below the fold. Without JavaScript a deferred image never loads.
const DEFERRED_MARKER = 'data-cms-deferred'
// src last when restoring: it starts the request.
const DEFERRED_ATTRIBUTES: readonly string[] = ['srcset', 'src']
const parked = (name: string) => `data-cms-${name}`
// What is deferred: an image, or a picture with its sources and image.
const PICTURE_OR_IMG = new RegExp(`<picture\\b[\\s\\S]*?<\\/picture\\s*>|${IMG_TAG.source}`, 'gi')
const DEFERRED_TAG = new RegExp(`<(?:source|img)\\b${TAG_BODY}>`, 'gi')

const deferTag = (tag: string) => tag
  .replace(ATTRIBUTE, (attribute, space: string, name: string, assignment?: string) =>
    assignment && DEFERRED_ATTRIBUTES.includes(name.toLowerCase()) ? space + parked(name) + assignment : attribute)
  .replace(/^<(\w+)/, (_, name: string) => `<${name.toLowerCase()} ${DEFERRED_MARKER}`)

// Images before `from` (an offset into html) are left as they are.
export const withDeferredImages = (html: string, from = 0): string =>
  html.replace(PICTURE_OR_IMG, (match, offset: number) => {
    const img = match.match(IMG_TAG)?.[0]
    return offset < from || !img || attributeValue(img, 'loading')?.toLowerCase() === 'eager'
      ? match
      : match.replace(DEFERRED_TAG, deferTag)
  })

// Offset just past the first <div> child of the <div> whose class list has
// `wrapperClass`. Only <div> nesting is counted, so an element of another kind
// before that child (a <p>) is skipped, and so is the text of a comment,
// <script> or <style>. Undefined when there is no such wrapper, it closes with no
// <div> child, or the markup ends first: the caller then decides (CmsContent
// defers nothing).
const DIV_TAG = new RegExp(`<!--[\\s\\S]*?-->|<(script|style)\\b${TAG_BODY}>[\\s\\S]*?<\\/\\1\\s*>|<\\/?div\\b${TAG_BODY}>`, 'gi')

const hasClass = (tag: string, className: string) =>
  attributeValue(tag, 'class')?.split(/\s+/).includes(className) ?? false

// The <div> tags of html that are markup, in order, with the nesting each one
// opens or returns to. Text elements and comments may hold <div> text that is
// not markup, so they are skipped. firstChildEnd and videoContainerRanges both
// read nesting through here, so they cannot disagree about the same payload.
function* markupDivs (html: string) {
  let depth = 0
  for (const { 0: tag, 1: textElement, index } of html.matchAll(DIV_TAG)) {
    if (textElement || tag.startsWith('<!--')) {
      continue
    }
    const opens = tag[1] !== '/'
    depth += opens ? 1 : -1
    yield { tag, index, opens, depth }
  }
}

export const firstChildEnd = (html: string, wrapperClass: string): number | undefined => {
  // The nesting inside the wrapper, once it is open.
  let inWrapper: number | undefined
  for (const { tag, index, opens, depth } of markupDivs(html)) {
    if (opens) {
      if (inWrapper === undefined && hasClass(tag, wrapperClass)) {
        inWrapper = depth
      }
      continue
    }
    // Back at the wrapper's nesting, its first child closed; below it, the
    // wrapper closed with no <div> child.
    if (inWrapper !== undefined && depth <= inWrapper) {
      return depth === inWrapper ? index + tag.length : undefined
    }
  }
}

// The video widget as the CMS authors it fails axe three ways (AB#65419): the
// player iframe has no title, the cover image has no alt, and the checkbox's id
// never matches its label's `for` (the CMS writes the video's URL tail into one),
// so the checkbox has no name. Inside each video container only: an untitled
// YouTube iframe gets YouTube's own embed title, an alt-less cover gets alt=""
// (it repeats the player under it), and an unnamed checkbox gets `revealLabel`.
// The checkbox is named rather than its label re-pointed: the label holds only
// the cover, now alt="", so it would still give no name. Nothing here relies on
// the for/id pair - the reveal CSS uses sibling selectors and useCmsVideoReveal
// reads focus - so the association is left as authored. A checkbox that does
// have a name keeps it: an aria-label, aria-labelledby, or a <label> that gives
// one - a <label for> in the same container that matches its id, or a <label>
// around it, holding text, an aria-label or an image with alt text. A label
// holding only the cover gives no name once the cover is alt="". Only an empty
// revealLabel is skipped (an empty name is worse than none); a key with no
// translation arrives as the key itself, since t() returns it on a miss, and
// becomes the name.
const VIDEO_WIDGET_TAG = new RegExp(`<(?:iframe|img|input)\\b${TAG_BODY}>`, 'gi')
// Labels do not nest, so a label runs to the first </label> after it.
const LABEL_ELEMENT = new RegExp(`<label\\b${TAG_BODY}>[\\s\\S]*?<\\/label\\s*>`, 'gi')
const ANY_TAG = new RegExp(`<!--[\\s\\S]*?-->|<\\/?[a-z][^\\s/>]*${TAG_BODY}>`, 'gi')
const YOUTUBE_SRC = /^(?:https?:)?\/\/(?:[\w-]+\.)*(?:youtube(?:-nocookie)?\.com|youtu\.be)\//i
const YOUTUBE_PLAYER_TITLE = 'YouTube video player'

// A function replacer, so a `$` in the value (a translation) is not read as a pattern.
const withAttribute = (tag: string, name: string, value: string) =>
  tag.replace(/^<(\w+)/, (_match, tagName: string) => `<${tagName} ${name}="${escapeHtml(value)}"`)

// [start, end) of each video container's markup, by <div> nesting as firstChildEnd
// counts it. One left open runs to the end of the markup.
const videoContainerRanges = (html: string): Array<[number, number]> => {
  const ranges: Array<[number, number]> = []
  let open: { start: number, depth: number } | undefined
  for (const { tag, index, opens, depth } of markupDivs(html)) {
    if (opens) {
      open ??= hasClass(tag, CMS_VIDEO_CONTAINER_CLASS) ? { start: index, depth } : undefined
      continue
    }
    if (open && depth < open.depth) {
      ranges.push([open.start, index + tag.length])
      open = undefined
    }
  }
  if (open) {
    ranges.push([open.start, html.length])
  }
  return ranges
}

const titledPlayer = (tag: string) =>
  attributeValue(tag, 'title') === undefined && YOUTUBE_SRC.test(attributeValue(tag, 'src') ?? '')
    ? withAttribute(tag, 'title', YOUTUBE_PLAYER_TITLE)
    : tag

const silentCover = (tag: string) =>
  attributeValue(tag, 'alt') === undefined && hasClass(tag, CMS_VIDEO_COVER_CLASS) ? withAttribute(tag, 'alt', '') : tag

const hasValue = (tag: string, name: string) => !!attributeValue(tag, name)?.trim()

// The label's content gives a name: text, an element's aria-label, or an image's
// alt. An image without alt gives none.
const givesName = (label: string) => {
  const content = label.slice(label.indexOf('>') + 1)
  return !!content.replace(ANY_TAG, '').replace(/&nbsp;|&#160;/gi, '').trim()
    || [...content.matchAll(ANY_TAG)].some(([tag]) =>
      hasValue(tag, 'aria-label') || (/^<img\b/i.test(tag) && hasValue(tag, 'alt')))
}

// labelledIds: the `for` of every <label> in the container that gives a name.
const isUnnamedCheckbox = (tag: string, labelledIds: Set<string>, inLabel: boolean) => {
  const id = attributeValue(tag, 'id')
  return attributeValue(tag, 'type')?.toLowerCase() === 'checkbox'
    && attributeValue(tag, 'aria-label') === undefined
    && attributeValue(tag, 'aria-labelledby') === undefined
    && !inLabel
    && (id === undefined || !labelledIds.has(id))
}

const accessibleVideoContainer = (markup: string, revealLabel: string) => {
  const labelledIds = new Set<string>()
  // [start, end) of each <label> that gives a name.
  const namingLabels: Array<[number, number]> = []
  for (const { 0: label, index } of markup.matchAll(LABEL_ELEMENT)) {
    if (!givesName(label)) {
      continue
    }
    namingLabels.push([index, index + label.length])
    const target = attributeValue(label.slice(0, label.indexOf('>') + 1), 'for')
    if (target !== undefined) {
      labelledIds.add(target)
    }
  }
  return markup.replace(VIDEO_WIDGET_TAG, (tag, offset: number) => {
    const name = tag.slice(1, tag.search(/[\s>]|\/>/)).toLowerCase()
    if (name === 'iframe') {
      return titledPlayer(tag)
    }
    if (name === 'img') {
      return silentCover(tag)
    }
    return revealLabel && isUnnamedCheckbox(tag, labelledIds, namingLabels.some(([start, end]) => offset > start && offset < end))
      ? withAttribute(tag, 'aria-label', revealLabel)
      : tag
  })
}

export const withAccessibleVideos = (html: string, revealLabel: string): string => {
  if (!html.includes(CMS_VIDEO_CONTAINER_CLASS)) {
    return html
  }
  let result = ''
  let from = 0
  for (const [start, end] of videoContainerRanges(html)) {
    result += html.slice(from, start) + accessibleVideoContainer(html.slice(start, end), revealLabel.trim())
    from = end
  }
  return result + html.slice(from)
}

export const restoreDeferredImages = (root: ParentNode) => {
  // Document order: a picture's sources are back before its image asks for a source.
  root.querySelectorAll(`[${DEFERRED_MARKER}]`).forEach((el) => {
    el.removeAttribute(DEFERRED_MARKER)
    DEFERRED_ATTRIBUTES.forEach((name) => {
      const value = el.getAttribute(parked(name))
      if (value !== null) {
        el.setAttribute(name, value)
        el.removeAttribute(parked(name))
      }
    })
  })
}
